import { createReducer, on } from '@ngrx/store';
import * as ReportActions from './report.actions';
import { Report, ReportExpenses, ReportSummary } from './report.model';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface ReportState {
    Report: Report;
    isLoading: boolean;
    isAvailable: boolean;
    error: string | null;
}

export interface ReportSummaryState {
    ReportSummary: ReportSummary;
    isLoading: boolean;
    error: string | null;
}

export interface ReportShowExpensesState {
    ReportShowExpenses: ReportExpenses;
    isLoading: boolean;
    error: string | null;
}

export interface ReportOfficeExpensesState {
    ReportOfficeExpenses: ReportExpenses;
    isLoading: boolean;
    error: string | null;
}

export interface ReportOnsiteExpensesState {
    ReportOnsiteExpenses: ReportExpenses;
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialReportState: ReportState = {
    Report: null,
    isLoading: false,
    isAvailable: false,
    error: null,
};

export const initialReportSummaryState: ReportSummaryState = {
    ReportSummary: null,
    isLoading: false,
    error: null,
};

export const initialReportShowExpensesState: ReportShowExpensesState = {
    ReportShowExpenses: null,
    isLoading: false,
    error: null,
};

export const initialReportOfficeExpensesState: ReportOfficeExpensesState = {
    ReportOfficeExpenses: null,
    isLoading: false,
    error: null,
};

export const initialReportOnsiteExpensesState: ReportOnsiteExpensesState = {
    ReportOnsiteExpenses: null,
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

// Report
export const reportReducer = createReducer(
    initialReportState,

    // On generate report
    on(ReportActions.generateReport, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(ReportActions.generateReportSuccess, (state, { report }) => ({
        ...state,
        isLoading: false,
        isAvailable: true,
        Report: report,
    })),
    on(ReportActions.generateReportFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        isAvailable: false,
        error: error,
    })),

    // Clear
    on(ReportActions.clearPreviousReport, (state) => ({
        ...state,
        isLoading: false,
        isAvailable: false,
        Report: null,
    }))
);

// 1. Report Summary
export const reportSummaryReducer = createReducer(
    initialReportSummaryState,

    // From Playdates
    on(ReportActions.getReportSummaryPlaydates, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        ReportActions.getReportSummaryPlaydatesSuccess,
        (state, { reportSummary }) => ({
            ...state,
            isLoading: false,
            ReportSummary: reportSummary,
        })
    ),
    on(ReportActions.getReportSummaryPlaydatesFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // From Date Range
    on(ReportActions.getReportSummaryDateRange, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        ReportActions.getReportSummaryDateRangeSuccess,
        (state, { reportSummary }) => ({
            ...state,
            isLoading: false,
            ReportSummary: reportSummary,
        })
    ),
    on(ReportActions.getReportSummaryDateRangeFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // Clear
    on(ReportActions.clearPreviousReport, (state) => ({
        ...state,
        isLoading: false,
        ReportSummary: {
            attendance: 0,
            expPerCap: 0,
            grossPerCap: 0,
            // profitPerCap: 0,
            // totalExpenses: 0,
            // totalGrossSales: 0,
            revenue: {
                boxOffice: 0,
                concession: 0,
                midway: 0,
                novelties: 0,
                others: 0,
                parking: 0,
            },
        },
    }))
);

// 2. Report Show Expenses
export const reportShowExpensesReducer = createReducer(
    initialReportShowExpensesState,

    // From Playdates
    on(ReportActions.getReportShowExpensesPlaydates, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        ReportActions.getReportShowExpensesPlaydatesSuccess,
        (state, { reportShowExpenses }) => ({
            ...state,
            isLoading: false,
            ReportShowExpenses: reportShowExpenses,
        })
    ),
    on(
        ReportActions.getReportShowExpensesPlaydatesFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // From Date Range
    on(ReportActions.getReportShowExpensesDateRange, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        ReportActions.getReportShowExpensesDateRangeSuccess,
        (state, { reportShowExpenses }) => ({
            ...state,
            isLoading: false,
            ReportShowExpenses: reportShowExpenses,
        })
    ),
    on(
        ReportActions.getReportShowExpensesDateRangeFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // Clear
    on(ReportActions.clearPreviousReport, (state) => ({
        ...state,
        isLoading: false,
        ReportShowExpenses: null,
    }))
);

// 3. Report Office Expenses
export const reportOfficeExpensesReducer = createReducer(
    initialReportOfficeExpensesState,

    // From Playdates
    on(ReportActions.getReportOfficeExpensesPlaydates, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        ReportActions.getReportOfficeExpensesPlaydatesSuccess,
        (state, { reportOfficeExpenses }) => ({
            ...state,
            isLoading: false,
            ReportOfficeExpenses: reportOfficeExpenses,
        })
    ),
    on(
        ReportActions.getReportOfficeExpensesPlaydatesFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // From Date Range
    on(ReportActions.getReportOfficeExpensesDateRange, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        ReportActions.getReportOfficeExpensesDateRangeSuccess,
        (state, { reportOfficeExpenses }) => ({
            ...state,
            isLoading: false,
            ReportOfficeExpenses: reportOfficeExpenses,
        })
    ),
    on(
        ReportActions.getReportOfficeExpensesDateRangeFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // Clear
    on(ReportActions.clearPreviousReport, (state) => ({
        ...state,
        isLoading: false,
        ReportOfficeExpenses: null,
    }))
);

// 4. Report Onsite Expenses
export const reportOnsiteExpensesReducer = createReducer(
    initialReportOnsiteExpensesState,

    // From Playdates
    on(ReportActions.getReportOnsiteExpensesPlaydates, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        ReportActions.getReportOnsiteExpensesPlaydatesSuccess,
        (state, { reportOnsiteExpenses }) => ({
            ...state,
            isLoading: false,
            ReportOnsiteExpenses: reportOnsiteExpenses,
        })
    ),
    on(
        ReportActions.getReportOnsiteExpensesPlaydatesFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // From Date Range
    on(ReportActions.getReportOnsiteExpensesDateRange, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        ReportActions.getReportOnsiteExpensesDateRangeSuccess,
        (state, { reportOnsiteExpenses }) => ({
            ...state,
            isLoading: false,
            ReportOnsiteExpenses: reportOnsiteExpenses,
        })
    ),
    on(
        ReportActions.getReportOnsiteExpensesDateRangeFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // Clear
    on(ReportActions.clearPreviousReport, (state) => ({
        ...state,
        isLoading: false,
        ReportOnsiteExpenses: null,
    }))
);
